import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import { AccessDenied } from '../../components/Error';
import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { isBudgetApprover } from '../../utils/functions/approverAccess';
import { useSocket } from '../../contexts/SocketContext';

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '-');

// รูปแบบแถบแท็บเดียวกับหน้า /finance/expenses (ExpensesList.js) — pill ไล่สี + ไอคอน + ตัวเลขจำนวน
const TABS = [
  {
    key: 'PENDING',
    label: 'รออนุมัติ',
    icon: HourglassTopRoundedIcon,
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
  },
  {
    key: 'APPROVED',
    label: 'อนุมัติแล้ว',
    icon: TaskAltRoundedIcon,
    color: '#16a34a',
    gradient: 'linear-gradient(135deg, #22c55e 0%, #0d9488 100%)',
  },
  {
    key: 'REJECTED',
    label: 'ปฏิเสธ',
    icon: HighlightOffRoundedIcon,
    color: '#dc2626',
    gradient: 'linear-gradient(135deg, #ef4444 0%, #be123c 100%)',
  },
];

const STATUS_CHIP = {
  PENDING: { label: 'รออนุมัติ', color: '#f59e0b' },
  APPROVED: { label: 'อนุมัติแล้ว', color: '#16a34a' },
  REJECTED: { label: 'ปฏิเสธ', color: '#dc2626' },
};

const HEAD_SX = { '& .MuiTableCell-root': { fontWeight: 700, bgcolor: 'action.hover', whiteSpace: 'nowrap' } };

// /profile/budget-approval — หน้าอนุมัติ budget ใหม่ของโครงการ (เมนู "อนุมัติ budget" กลุ่ม "การอนุมัติ" ใต้เมนูโครงการ)
// 3 แท็บ: รออนุมัติ (มีปุ่มอนุมัติ/ปฏิเสธ ปฏิเสธต้องใส่เหตุผล) / อนุมัติแล้ว / ปฏิเสธ — แท็บ "อนุมัติแล้ว" แสดงเฉพาะที่
// ผ่านขั้นนี้จริง ไม่รวม budget เดิมก่อนมีฟีเจอร์อนุมัติ. สิทธิ์: ต้องอยู่ในรายชื่อ approver_settings key 'budget_approver'
export default function BudgetApproval({ title }) {
  const me = useSelector((state) => state.me);
  const info = useSelector((state) => state.info);
  const socket = useSocket();
  const allowed = isBudgetApprover(me, info);

  const [tab, setTab] = useState('PENDING');
  const [lists, setLists] = useState({ PENDING: [], APPROVED: [], REJECTED: [] });
  const [search, setSearch] = useState('');
  const [rejectId, setRejectId] = useState(null);
  const [reason, setReason] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const results = await Promise.all(
        TABS.map(({ key }) => api.get(`${process.env.REACT_APP_API_URL}/budget/pending-approval?status=${key}`)),
      );
      setLists(Object.fromEntries(TABS.map(({ key }, i) => [key, results[i]?.data?.rows || []])));
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  // budget ถูกสร้าง/อนุมัติจากที่อื่น → ดึงรายการใหม่ (budget:updated ยิงจาก routes/api/budget.js ทุก non-GET)
  useEffect(() => {
    if (!allowed || !socket) return undefined;
    socket.on('budget:updated', load);
    return () => socket.off('budget:updated', load);
  }, [allowed, socket, load]);

  const activeTab = TABS.find((t) => t.key === tab);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = lists[tab] || [];
    if (!q) return list;
    return list.filter((r) =>
      [r.budget_number, r.name, r.description, r.project?.project_number, r.project?.name]
        .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [lists, tab, search]);
  const total = rows.reduce((s, r) => s + (Number(r.cost) || 0), 0);

  if (!allowed) {
    return <AccessDenied moduleLabel={title || 'อนุมัติ budget'} />;
  }

  const isPendingTab = tab === 'PENDING';
  const colCount = isPendingTab ? 8 : (tab === 'REJECTED' ? 11 : 10);

  const decide = async (row, action) => {
    if (action === 'reject' && !reason.trim()) {
      showAlert('กรุณาระบุเหตุผลที่ปฏิเสธ');
      return;
    }
    const confirmText = action === 'cancel'
      ? `ยกเลิก${row.approval_status === 'REJECTED' ? 'การปฏิเสธ' : 'การอนุมัติ'} budget ${row.budget_number} และกลับไปเป็นรออนุมัติ ?`
      : `${action === 'approve' ? 'อนุมัติ' : 'ปฏิเสธ'} budget ${row.budget_number} ?`;
    if (!(await confirmAsync(confirmText))) return;
    setBusyId(row._id);
    try {
      await api.patch(`${process.env.REACT_APP_API_URL}/budget/${row._id}/approval`, {
        action,
        reason: action === 'reject' ? reason.trim() : undefined,
      });
      setRejectId(null);
      setReason('');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const renderActions = (row) => {
    if (rejectId === row._id) {
      return (
        <Box sx={{ minWidth: 240 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="เหตุผลที่ปฏิเสธ"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 0.75 }}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={busyId === row._id}
              onClick={() => decide(row, 'reject')}
              sx={{ borderRadius: 999, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
            >
              ยืนยันปฏิเสธ
            </Button>
            <Button size="small" sx={{ borderRadius: 999 }} onClick={() => { setRejectId(null); setReason(''); }}>
              ยกเลิก
            </Button>
          </Box>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={busyId === row._id}
          onClick={() => decide(row, 'approve')}
          sx={{ borderRadius: 999, whiteSpace: 'nowrap', bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
        >
          อนุมัติ
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={busyId === row._id}
          onClick={() => { setRejectId(row._id); setReason(''); }}
          sx={{ borderRadius: 999, whiteSpace: 'nowrap', color: '#dc2626', borderColor: '#dc2626' }}
        >
          ปฏิเสธ
        </Button>
      </Box>
    );
  };

  return (
    <div>
      {/* แถบแท็บ — สไตล์เดียวกับ /finance/expenses */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 0.75,
            p: 0.75,
            borderRadius: '18px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.35)' : '0 4px 20px rgba(15,23,42,0.06)',
          }}
        >
          {TABS.map((t) => {
            const selected = tab === t.key;
            const Icon = t.icon;
            return (
              <Box
                key={t.key}
                onClick={() => { setTab(t.key); setRejectId(null); setReason(''); }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.9,
                  px: 2,
                  py: 1,
                  borderRadius: '14px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  transition: 'transform 0.2s ease, background-color 0.2s ease, box-shadow 0.25s ease',
                  transform: selected ? 'translateY(-1px)' : 'none',
                  background: selected ? t.gradient : 'transparent',
                  boxShadow: selected ? '0 6px 16px -4px rgba(0,0,0,0.35)' : 'none',
                  '&:hover': {
                    background: selected
                      ? t.gradient
                      : (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)'),
                  },
                }}
              >
                <Icon sx={{ fontSize: 19, color: selected ? '#fff' : 'text.secondary' }} />
                <Typography sx={{ fontSize: '0.85rem', fontWeight: selected ? 700 : 500, color: selected ? '#fff' : 'text.secondary' }}>
                  {t.label}
                </Typography>
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.7,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '999px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    bgcolor: selected
                      ? 'rgba(255,255,255,0.28)'
                      : (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.07)'),
                    color: selected ? '#fff' : 'text.secondary',
                  }}
                >
                  {lists[t.key].length}
                </Box>
              </Box>
            );
          })}
        </Paper>
      </Box>

      {/* ค้นหา + สรุป */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="ค้นหา เลข budget / ชื่อ / โครงการ"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 300, '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {rows.length} รายการ · รวม{' '}
          <Box component="span" sx={{ fontWeight: 800, color: activeTab.color, fontVariantNumeric: 'tabular-nums' }}>
            ฿{fmtMoney(total)}
          </Box>
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={HEAD_SX}>
              <TableCell>เลข budget</TableCell>
              <TableCell>โครงการ</TableCell>
              <TableCell>ชื่อ</TableCell>
              <TableCell>รายละเอียด</TableCell>
              <TableCell align="right">งบประมาณ</TableCell>
              <TableCell>วันที่สร้าง</TableCell>
              <TableCell align="center">สถานะ</TableCell>
              {isPendingTab ? (
                <TableCell align="center">จัดการ</TableCell>
              ) : (
                <>
                  <TableCell>{tab === 'APPROVED' ? 'ผู้อนุมัติ' : 'ผู้ปฏิเสธ'}</TableCell>
                  <TableCell>วันที่{tab === 'APPROVED' ? 'อนุมัติ' : 'ปฏิเสธ'}</TableCell>
                  {tab === 'REJECTED' && <TableCell>เหตุผล</TableCell>}
                  <TableCell align="center">จัดการ</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  ไม่มีรายการ
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => {
              const st = STATUS_CHIP[tab];
              return (
                <TableRow key={row._id} hover sx={{ verticalAlign: 'top' }}>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {row.budget_number}
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.project?.project_number || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.project?.name}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>{row.name}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', minWidth: 160 }}>{row.description || '-'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    ฿{fmtMoney(row.cost)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{fmtDate(row.createdAt)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={st.label}
                      sx={{ fontWeight: 700, color: st.color, bgcolor: alpha(st.color, 0.12) }}
                    />
                  </TableCell>
                  {isPendingTab ? (
                    <TableCell align="center">{renderActions(row)}</TableCell>
                  ) : (
                    <>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.approval_by_name || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{fmtDate(row.approval_at)}</TableCell>
                      {tab === 'REJECTED' && (
                        <TableCell sx={{ color: '#dc2626', minWidth: 160 }}>{row.approval_reason || '-'}</TableCell>
                      )}
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busyId === row._id}
                          onClick={() => decide(row, 'cancel')}
                          sx={{ borderRadius: 999, whiteSpace: 'nowrap', color: '#f59e0b', borderColor: '#f59e0b' }}
                        >
                          ยกเลิก
                        </Button>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
